import React from 'react';
import { Label } from '@/components/shared/label';
import { Slider } from '@/components/shared/slider';
import { RadioGroup, RadioGroupItem } from '@/components/shared/radio-group';
import { useIsMobile } from '@/hooks/use-mobile';

interface ValueSliderProps {
  label: string;
  subtext: string;
  value: number;
  onChange: (value: number[]) => void;
  isChallengeMetric?: boolean;
  isLastItem?: boolean;
}

const ValueSlider = ({ label, subtext, value, onChange, isChallengeMetric = false, isLastItem = false }: ValueSliderProps) => {
  const isMobile = useIsMobile();
  const isDev = import.meta.env.DEV;

  const getPriorityLabel = (value: number) => {
    if (isChallengeMetric) {
      switch (value) {
        case 1:
          return 'Very Low Concern';
        case 2:
          return 'Low Concern';
        case 3:
          return 'Moderate Concern';
        case 4:
          return 'High Concern';
        case 5:
          return 'Very High Concern';
        default:
          return 'Moderate Concern';
      }
    }
    
    switch (value) {
      case 1:
        return 'Very Low';
      case 2:
        return 'Low';
      case 3:
        return 'Moderate';
      case 4:
        return 'High';
      case 5:
        return 'Critical';
      default:
        return 'Moderate';
    }
  };

  const getShortLabel = (value: number) => {
    if (isChallengeMetric) {
      switch (value) {
        case 1:
          return 'Very Low';
        case 2:
          return 'Low';
        case 3:
          return 'Moderate';
        case 4:
          return 'High';
        case 5:
          return 'Very High';
        default:
          return 'Moderate';
      }
    }
    
    switch (value) {
      case 1:
        return 'Very Low';
      case 2:
        return 'Low';
      case 3:
        return 'Moderate';
      case 4:
        return 'High';
      case 5:
        return 'Critical';
      default:
        return 'Moderate';
    }
  };

  const handleRadioChange = (value: string) => {
    onChange([parseInt(value)]);
  };

  // Old UI for production
  if (!isDev) {
    return (
      <div className={`space-y-4 ${isLastItem ? '' : 'border-b border-gray-300'} pb-6`}>
        <div className="">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
            <Label className="text-xl font-medium text-gray-800">{label}</Label>
            <span className="hidden sm:block py-1 px-3 text-xl text-[#CA0079] bg-white rounded-md self-start sm:self-auto">
              {isChallengeMetric ? getPriorityLabel(value) : getPriorityLabel(value) + " Priority"}
            </span>
          </div>
          <p className="text-md text-gray-600 font-light min-h-[48px] leading-relaxed">{subtext}</p>
        </div>

        {isMobile ? (
          <div className="mt-4">
            <RadioGroup
              value={value.toString()}
              onValueChange={handleRadioChange}
              className="flex flex-col space-y-3"
            >
              {[1, 2, 3, 4, 5].map((val) => (
                <div key={val} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value={val.toString()} id={`${label}-${val}`} />
                  <Label htmlFor={`${label}-${val}`} className="text-sm font-medium flex-1">
                    {getPriorityLabel(val)}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        ) : (
          <div className="flex items-center gap-4 pt-2">
            <span className="text-md text-gray-900 font-light min-w-[100px]">
              {isChallengeMetric ? "Very Low Concern" : "Very Low"}
            </span>
            <Slider
              value={[value]}
              min={1}
              max={5}
              step={1}
              onValueChange={onChange}
              className="flex-1"
            />
            <span className="text-md text-gray-900 font-light min-w-[100px] text-right">
              {isChallengeMetric ? "Very High Concern" : "Critical"}
            </span>
          </div>
        )}
      </div>
    );
  }

  // New UI for development
  return (
    <div className={`space-y-4 ${isLastItem ? '' : 'border-b border-gray-300'} pb-8`}>
      <div className="">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
          <Label className="text-xl font-medium text-gray-800">{label}</Label>
          <span className="hidden sm:block py-1 px-3 text-xl text-[#CA0079] bg-white rounded-md self-start sm:self-auto">
            {isChallengeMetric ? getPriorityLabel(value) : getPriorityLabel(value) + " Priority"}
          </span>
        </div>
        <p className="text-md text-gray-600 font-light min-h-[48px] leading-relaxed">{subtext}</p>
      </div>

      {isMobile ? (
        <div className="mt-4">
          <RadioGroup
            value={value.toString()}
            onValueChange={handleRadioChange}
            className="flex flex-col space-y-3"
          >
            {[1, 2, 3, 4, 5].map((val) => (
              <div key={val} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                <RadioGroupItem value={val.toString()} id={`${label}-${val}`} />
                <Label htmlFor={`${label}-${val}`} className="text-sm font-medium flex-1">
                  {getPriorityLabel(val)}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Slider with tick marks underneath */}
          <div className="relative">
            <Slider
              value={[value]}
              min={1}
              max={5}
              step={1}
              onValueChange={onChange}
              className="flex-1"
            />
            
            {/* Tick marks and labels - positioned underneath the slider */}
            <div className="relative mt-4 mb-8">
              {[1, 2, 3, 4, 5].map((tickValue) => {
                // The track is inset by 14px on each side due to the slider padding
                // So we need to adjust our positioning to account for this
                const basePercentage = ((tickValue - 1) / 4) * 100; // 0%, 25%, 50%, 75%, 100%
                // The track now spans from 14px to (100% - 14px) of the container
                // We need to map our percentages to this new range
                const thumb_offset = 2.25;
                const trackStart = thumb_offset; // 14px from left
                const trackEnd = 100 - thumb_offset; // 14px from right
                const trackWidth = trackEnd - trackStart;
                const percentage = trackStart + (basePercentage / 100) * trackWidth;
                
                return (
                  <div 
                    key={tickValue} 
                    className="absolute flex flex-col items-center transform -translate-x-1/2"
                    style={{ left: `${percentage}%` }}
                  >
                    {/* Tick mark */}
                    <div className="w-0.5 h-2 bg-gray-300 mb-1"></div>
                    {/* Label */}
                    <span className="text-xs text-gray-500 font-medium text-center leading-tight whitespace-nowrap">
                      {getShortLabel(tickValue)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValueSlider;